import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";
import type { ScheduleWizardDraft } from "~reports/schedules/helpers/scheduleWizardDraft";

import isRecord from "~/helpers/isRecord";

export const REPORT_SCHEDULE_SOURCE_KIND = "reportSchedule";

export const REPORT_SCHEDULE_TARGET_KIND = "reportSchedule";

export const REPORTS_BASE_PATH = "/reports";

export const REPORT_SCHEDULES_PATH = "/reports/schedules";

export const REPORTS_DOCK_PAGE_ID = "reports";

const REPORT_SCHEDULE_ROUTE_PATTERN = /^\/reports\/schedules\/([^/?#]+)$/;

export interface ReportScheduleSourceCheckpoint {
	activeStep: number;
	baseline: ScheduleWizardDraft;
	createdScheduleId?: null | string;
	draft: ScheduleWizardDraft;
	scheduleId: null | string;
	scheduleName: string;
}

const isScheduleWizardDraft = (value: unknown): value is ScheduleWizardDraft =>
	isRecord(value) &&
	typeof value.reportId === "string" &&
	typeof value.scheduleName === "string" &&
	typeof value.senderId === "string" &&
	typeof value.frequency === "string" &&
	typeof value.time === "string" &&
	typeof value.startDate === "string" &&
	typeof value.endDate === "string" &&
	Array.isArray(value.recipients) &&
	Array.isArray(value.daysOfWeek) &&
	isRecord(value.monthAndDay) &&
	isRecord(value.parameters);

export const isReportScheduleSourceCheckpoint = (value: unknown): value is ReportScheduleSourceCheckpoint =>
	isRecord(value) &&
	typeof value.activeStep === "number" &&
	(value.createdScheduleId === undefined ||
		value.createdScheduleId === null ||
		typeof value.createdScheduleId === "string") &&
	(value.scheduleId === null || typeof value.scheduleId === "string") &&
	typeof value.scheduleName === "string" &&
	isScheduleWizardDraft(value.draft) &&
	isScheduleWizardDraft(value.baseline);

export const buildReportSchedulePath = (scheduleId: string): string =>
	`${REPORT_SCHEDULES_PATH}/${encodeURIComponent(scheduleId)}`;

export const matchReportScheduleReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = REPORT_SCHEDULE_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind: REPORT_SCHEDULE_TARGET_KIND };
	} catch {
		return null;
	}
};

export const reportScheduleReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildReportSchedulePath(targetId),
	matchesRoute: matchReportScheduleReceiverRoute
};

const reportScheduleEditorKey = (scheduleId: null | string): string => `report-schedule:${scheduleId ?? "new"}`;

export default reportScheduleEditorKey;
